<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { useCursor } from "../../composables/useCursor";
import { useDrawingQueue } from "../../composables/useDrawingQueue";
import { usePixelCoordinates } from "../../composables/usePixelCoordinates";
import { useAvatarStore } from "../../stores/useAvatarStore";
import { useCanvasDrawing } from "./composables/useCanvasDrawing";
import { useCanvasTransform } from "./composables/useCanvasTransform";
import { exportToPng, renderCanvas } from "./utils/canvasRendering";

interface Props {
  width: number;
  height: number;
  pixels: Uint32Array;
  showGrid: boolean;
  selectedTool: string;
  currentColor: string;
  brushSize: number;
  pixelSize: number;
  selectedLayerId: number;
  viewMode: "pixel" | "raw";
}

const props = defineProps<Props>();
const emit = defineEmits<{
  (e: "pixel-update", index: number, newPixels?: Uint32Array): void;
  (e: "drawing-start"): void;
  (e: "drawing-end"): void;
  (e: "mousedown"): void;
  (e: "mouseup"): void;
  (e: "mousemove"): void;
}>();

// Canvas references
const canvasRef = ref<HTMLCanvasElement | null>(null);
const ctx = ref<CanvasRenderingContext2D | null>(null);
const needsRender = ref(false);
let animationFrameId: number | null = null;

// 性能监控
const lastRenderTime = ref<number>(0);
const lastMouseEvent = ref<MouseEvent | null>(null);
const hasNewMouseEvent = ref(false);
const fps = ref(0);
let frameCount = 0;
let lastFPSTime = performance.now();

// Import composables
const { isDrawing, startDrawing, stopDrawing } = useCanvasDrawing();
const {
  scale,
  panPosition,
  transformStyle,
  scaleStyle,
  handleWheel,
  handleMouseDown: handlePanStart,
  handleMouseMove: handlePanMove,
  handleMouseUp: handlePanEnd,
  handleKeyDown: handlePanKeyDown,
  handleKeyUp: handlePanKeyUp,
  isSpaceKeyPressed,
} = useCanvasTransform();

const { width: windowWidth, height: windowHeight } = useWindowSize();
const canvasSize = computed<{
  width: string;
  height: string;
} | null>(() => {
  if (!canvasRef.value) return null;
  // Nav + Bottom + CanvasWidth > CanvasHeight => 高度溢出屏幕
  if (windowWidth.value + 66 + 140 > windowHeight.value) {
    const properHeight = windowWidth.value - 66 - 140;
    console.log(properHeight);
    return {
      width: `${properHeight}px`,
      height: `${properHeight}px`,
    };
  } else {
    console.log("null");
    return null;
  }
});

// Initialize coordinate handling
const coordinates = usePixelCoordinates(props.width, props.pixelSize, {
  scale,
  panPosition,
});

// Initialize drawing queue with state
const drawingState = {
  currentColor: props.currentColor,
  brushSize: props.brushSize,
  selectedTool: props.selectedTool,
  width: props.width,
};

const drawingQueue = useDrawingQueue(props.pixelSize, drawingState);

// Watch for changes in drawing state
watch(
  () => props.currentColor,
  (newColor) => {
    drawingState.currentColor = newColor;
  }
);

watch(
  () => props.brushSize,
  (newSize) => {
    drawingState.brushSize = newSize;
  }
);

watch(
  () => props.selectedTool,
  (newTool) => {
    drawingState.selectedTool = newTool;
  }
);

// Initialize cursor handling
const cursor = useCursor(props.pixelSize, scale);

// Update cursor when relevant props change
watch(
  [
    () => props.currentColor,
    () => props.selectedTool,
    () => props.brushSize,
    () => scale.value,
  ],
  () => {
    cursor.updateCursor(canvasRef.value, {
      currentColor: props.currentColor,
      brushSize: props.brushSize,
      selectedTool: props.selectedTool,
    });
  }
);

const store = useAvatarStore();
// Watch pixels changes to trigger render
watch(
  () => store.selectedLayer!.version,
  () => {
    needsRender.value = true;
  },
  { deep: true }
);

// Add touch state interface and initialization before the setup script
interface TouchState {
  initialDistance: number | null;
  initialScale: number | null;
  center: { x: number; y: number } | null;
}

const touchState: TouchState = {
  initialDistance: null,
  initialScale: null,
  center: null,
};
// 统一的渲染和事件处理循环
const mainLoop = () => {
  const currentTime = performance.now();

  // 更新 FPS
  frameCount++;
  if (currentTime - lastFPSTime >= 1000) {
    fps.value = Math.round((frameCount * 1000) / (currentTime - lastFPSTime));
    frameCount = 0;
    lastFPSTime = currentTime;
  }

  // 处理鼠标事件
  if (hasNewMouseEvent.value && lastMouseEvent.value) {
    if (isDrawing.value) {
      const { x, y } = coordinates.getMouseCanvasPosition(
        lastMouseEvent.value,
        canvasRef.value
      );

      drawingQueue.handleDrawingPoint(x, y, store.selectedLayer!.pixels);
      store.selectedLayer!.version++;
    }

    hasNewMouseEvent.value = false;
  }

  // 渲染画布
  if (needsRender.value && ctx.value) {
    const startTime = performance.now();

    renderCanvas(ctx.value, {
      width: props.width,
      height: props.height,
      pixelSize: props.pixelSize,
      pixels: props.pixels,
      showGrid: props.showGrid,
      viewMode: props.viewMode,
    });

    lastRenderTime.value = performance.now() - startTime;
    needsRender.value = false;
  }

  animationFrameId = requestAnimationFrame(mainLoop);
};

// Handle mouse events
const handleMouseDown = (event: MouseEvent) => {
  // If space is pressed, don't handle drawing
  if (isSpaceKeyPressed.value) {
    return;
  }

  // Get the pixel index at the mouse position
  const index = coordinates.getPixelIndex(event, canvasRef.value);
  // Return if click is outside canvas bounds
  if (index < 0) return;

  // Only handle left mouse button clicks
  if (event.button !== 0) return;

  // Special handling for eyedropper and fill tools
  if (props.selectedTool === "eyedropper" || props.selectedTool === "fill") {
    // Emit pixel update event with the clicked index
    emit("pixel-update", index);
    return;
  }

  startDrawing();
  emit("drawing-start");
  const { x, y } = coordinates.getMouseCanvasPosition(event, canvasRef.value);

  drawingQueue.handleDrawingPoint(x, y, store.selectedLayer!.pixels);
  store.selectedLayer!.version++;
};

const handleMouseMove = (event: MouseEvent) => {
  // Only update drawing events if space is not pressed
  if (!isSpaceKeyPressed.value) {
    lastMouseEvent.value = event;
    hasNewMouseEvent.value = true;
  }
};

const handleMouseUp = () => {
  // Only handle drawing end if space is not pressed
  if (!isSpaceKeyPressed.value && isDrawing.value) {
    stopDrawing();
    emit("drawing-end");
    drawingQueue.resetDrawingState();
  }
};

const handleTouchStart = (event: TouchEvent) => {
  event.preventDefault();

  // Handle two-finger gestures
  if (event.touches.length === 2) {
    const touch1 = event.touches[0];
    const touch2 = event.touches[1];

    // Calculate initial distance between fingers
    const initialDistance = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );

    // Store the initial distance and center point
    touchState.initialDistance = initialDistance;
    touchState.initialScale = scale.value;
    touchState.center = {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };
    return;
  }

  // If space is pressed, handle panning
  if (isSpaceKeyPressed.value) {
    const touch = event.touches[0];
    const mouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      preventDefault: () => {},
    } as MouseEvent;
    handlePanStart(mouseEvent);
    return;
  }

  // Get the pixel index at the touch position
  const touch = event.touches[0];
  const index = coordinates.getPixelIndex(touch, canvasRef.value);

  // Return if touch is outside canvas bounds
  if (index < 0) return;

  // Special handling for eyedropper and fill tools
  if (props.selectedTool === "eyedropper" || props.selectedTool === "fill") {
    emit("pixel-update", index);
    return;
  }

  startDrawing();
  emit("drawing-start");
  const { x, y } = coordinates.getMouseCanvasPosition(touch, canvasRef.value);

  drawingQueue.handleDrawingPoint(x, y, store.selectedLayer!.pixels);
  store.selectedLayer!.version++;
};

const handleTouchMove = (event: TouchEvent) => {
  event.preventDefault();

  // Handle two-finger gestures
  if (event.touches.length === 2) {
    const touch1 = event.touches[0];
    const touch2 = event.touches[1];

    // Calculate current distance between fingers
    const currentDistance = Math.hypot(
      touch2.clientX - touch1.clientX,
      touch2.clientY - touch1.clientY
    );

    // Calculate new scale based on the change in distance
    if (touchState.initialDistance && touchState.initialScale) {
      const scaleFactor = currentDistance / touchState.initialDistance;
      scale.value = touchState.initialScale * scaleFactor;
    }

    // Calculate center point for panning
    const currentCenter = {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2,
    };

    // Update pan position based on center point movement
    if (touchState.center) {
      panPosition.value.x += currentCenter.x - touchState.center.x;
      panPosition.value.y += currentCenter.y - touchState.center.y;
      touchState.center = currentCenter;
    }
    return;
  }

  // Handle panning if space is pressed
  if (isSpaceKeyPressed.value) {
    const touch = event.touches[0];
    const mouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      preventDefault: () => {},
    } as MouseEvent;
    handlePanMove(mouseEvent);
    return;
  }

  const touch = event.touches[0];
  lastMouseEvent.value = {
    clientX: touch.clientX,
    clientY: touch.clientY,
    preventDefault: () => {},
  } as MouseEvent;
  hasNewMouseEvent.value = true;
};

const handleTouchEnd = (event: TouchEvent) => {
  event.preventDefault();

  // Reset touch state
  touchState.initialDistance = null;
  touchState.initialScale = null;
  touchState.center = null;

  // Handle pan end if space is pressed
  if (isSpaceKeyPressed.value) {
    const touch = event.changedTouches[0];
    const mouseEvent = {
      clientX: touch.clientX,
      clientY: touch.clientY,
      preventDefault: () => {},
    } as MouseEvent;
    handlePanEnd(mouseEvent);
    return;
  }

  if (isDrawing.value) {
    stopDrawing();
    emit("drawing-end");
    drawingQueue.resetDrawingState();
  }
};

// Add keyboard event listeners
onMounted(async () => {
  await nextTick();
  if (!canvasRef.value) return;

  // Add keyboard event listeners
  window.addEventListener("keydown", handlePanKeyDown);
  window.addEventListener("keyup", handlePanKeyUp);

  // Disable zoom on mobile devices
  document.addEventListener(
    "touchstart",
    (e) => {
      if (e.touches.length > 1) {
        e.preventDefault();
      }
    },
    { passive: false }
  );

  // Improve touch precision
  if (canvasRef.value) {
    canvasRef.value.style.touchAction = "none";
  }

  ctx.value = canvasRef.value.getContext("2d", {
    willReadFrequently: true,
  });

  if (!ctx.value) return;

  ctx.value.imageSmoothingEnabled = true;

  renderCanvas(ctx.value, {
    width: props.width,
    height: props.height,
    pixelSize: props.pixelSize,
    pixels: props.pixels,
    showGrid: props.showGrid,
    viewMode: props.viewMode,
  });

  cursor.updateCursor(canvasRef.value, {
    currentColor: props.currentColor,
    brushSize: props.brushSize,
    selectedTool: props.selectedTool,
  });

  // 启动主循环
  mainLoop();
});

// Clean up on unmount
onUnmounted(() => {
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }

  // Remove keyboard event listeners
  window.removeEventListener("keydown", handlePanKeyDown);
  window.removeEventListener("keyup", handlePanKeyUp);
});

// Expose methods to parent component
defineExpose({
  exportToPng: () => {
    return canvasRef.value && ctx.value
      ? exportToPng(props.width, props.height, props.pixels)
      : null;
  },
});
</script>

<template>
  <!-- <PerformanceMonitor :render-time="lastRenderTime" :fps="fps" /> -->
  <div
    element
    class="[will-change:transform] flex-1 flex items-center justify-center w-full h-full bg-base-200"
    :style="transformStyle"
  >
    <canvas
      id="render-canvas"
      ref="canvasRef"
      :width="width * pixelSize"
      :height="height * pixelSize"
      :style="[
        scaleStyle,
        {
          backgroundImage:
            'linear-gradient(45deg, #e5e5e5 25%, transparent 25%, transparent 75%, #e5e5e5 75%, #e5e5e5), linear-gradient(45deg, #e5e5e5 25%, transparent 25%, transparent 75%, #e5e5e5 75%, #e5e5e5)',
          backgroundSize: '128px 128px',
          backgroundPosition: '0 0, 64px 64px',
          willChange: 'background-image',
          ...(canvasSize ?? {}),
        },
      ]"
      class="border-base-300 bg-white cursor-pointer [image-rendering:pixelated] [image-rendering:crisp-edges] [will-change:transform] w-full aspect-square"
      @wheel="handleWheel"
      @contextmenu.prevent
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
    >
    </canvas>
  </div>
</template>
